import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  Button, 
  Divider,
  IconButton,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import PrintIcon from '@mui/icons-material/Print';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// 탭 패널 컴포넌트
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportPage = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [reportType, setReportType] = useState('monthly');
  const [reportDate, setReportDate] = useState(new Date());
  const [activityType, setActivityType] = useState('all');
  
  // 탭 변경 핸들러
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // 보고서 유형 변경 핸들러
  const handleReportTypeChange = (event) => {
    setReportType(event.target.value);
  };
  
  // 활동 유형 변경 핸들러
  const handleActivityTypeChange = (event) => {
    setActivityType(event.target.value);
  };
  
  // 보고서 생성 핸들러
  const handleGenerateReport = () => {
    console.log('보고서 생성:', {
      reportType,
      reportDate,
      activityType
    });
    // 실제 앱에서는 여기서 API 호출을 통해 보고서 데이터를 가져옴
  };
  
  // 차트 데이터
  const monthlyEmissionData = [
    { name: '1주차', 배출량: 12.5 },
    { name: '2주차', 배출량: 13.2 },
    { name: '3주차', 배출량: 11.8 },
    { name: '4주차', 배출량: 12.3 },
  ];
  
  const emissionBySourceData = [
    { name: '교통', value: 35 },
    { name: '전기', value: 25 },
    { name: '난방', value: 20 },
    { name: '식품', value: 15 },
    { name: '기타', value: 5 },
  ];
  
  const COLORS = [
    theme.palette.chart.c1,
    theme.palette.chart.c2,
    theme.palette.chart.c3,
    theme.palette.chart.c4,
    theme.palette.chart.c5,
  ];
  
  const detailedEmissionData = [
    { id: 1, date: '2025-05-01', activity: '교통', quantity: '45 km', emission: 8.55 },
    { id: 2, date: '2025-05-03', activity: '전기', quantity: '12.5 kWh', emission: 5.75 },
    { id: 3, date: '2025-05-05', activity: '난방', quantity: '3.2 m³', emission: 7.10 },
    { id: 4, date: '2025-05-08', activity: '식품', quantity: '2.5 kg', emission: 4.25 },
    { id: 5, date: '2025-05-10', activity: '교통', quantity: '30 km', emission: 5.70 },
    { id: 6, date: '2025-05-12', activity: '전기', quantity: '10.8 kWh', emission: 4.97 },
    { id: 7, date: '2025-05-15', activity: '식품', quantity: '1.8 kg', emission: 3.06 },
    { id: 8, date: '2025-05-18', activity: '교통', quantity: '50 km', emission: 9.50 },
    { id: 9, date: '2025-05-20', activity: '전기', quantity: '11.2 kWh', emission: 5.15 },
    { id: 10, date: '2025-05-22', activity: '난방', quantity: '2.8 m³', emission: 6.22 },
  ];
  
  return (
    <Box sx={{ flexGrow: 1, py: 0 }}>
      {/* <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        탄소 배출량 보고서
      </Typography> */}
      
      <Grid container spacing={3}>
        {/* 보고서 설정 카드 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="보고서 설정" 
              subheader="보고서 매개변수를 설정하세요"
            />
            <Divider />
            <CardContent>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="report-type-label">보고서 유형</InputLabel>
                      <Select
                        labelId="report-type-label"
                        id="report-type"
                        value={reportType}
                        label="보고서 유형"
                        onChange={handleReportTypeChange}
                      >
                        <MenuItem value="daily">일간 보고서</MenuItem>
                        <MenuItem value="weekly">주간 보고서</MenuItem>
                        <MenuItem value="monthly">월간 보고서</MenuItem>
                        <MenuItem value="quarterly">분기 보고서</MenuItem>
                        <MenuItem value="yearly">연간 보고서</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="activity-type-label">활동 유형</InputLabel>
                      <Select
                        labelId="activity-type-label"
                        id="activity-type"
                        value={activityType}
                        label="활동 유형"
                        onChange={handleActivityTypeChange}
                      >
                        <MenuItem value="all">모든 활동</MenuItem>
                        <MenuItem value="transportation">교통</MenuItem>
                        <MenuItem value="electricity">전기</MenuItem>
                        <MenuItem value="heating">난방</MenuItem>
                        <MenuItem value="food">식품</MenuItem>
                        <MenuItem value="other">기타</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <DatePicker
                      format='yyyy-MM-dd'
                      label="보고서 날짜"
                      value={reportDate}
                      onChange={(newValue) => setReportDate(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: '보고서 기간 선택'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      onClick={handleGenerateReport}
                      sx={{ mt: 2 }}
                    >
                      보고서 생성
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </CardContent>
          </Card>
          
          {/* 보고서 요약 카드 */}
          <Card sx={{ mt: 3 }}>
            <CardHeader 
              title="보고서 요약" 
              subheader="2025년 5월"
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    총 배출량
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                    49.8 tCO₂e
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    전월 대비
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold', color: theme.palette.success.main }}>
                    -7.2%
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    최대 배출원
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    교통 (35%)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    목표 달성률
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    68%
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button startIcon={<DownloadIcon />} variant="outlined" size="small">
                  PDF 다운로드
                </Button>
                <Button startIcon={<ShareIcon />} variant="outlined" size="small">
                  공유
                </Button>
                <Button startIcon={<PrintIcon />} variant="outlined" size="small">
                  인쇄
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 보고서 내용 카드 */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="report tabs"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 100,
                    py: 2
                  }
                }}
              >
                <Tab label="개요" />
                <Tab label="상세 데이터" />
                <Tab label="추세 분석" />
              </Tabs>
            </Box>
            
            {/* 개요 탭 */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
                    월간 배출량 추이
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={monthlyEmissionData}
                        margin={{ top: 15, right: 10, left: 20, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: theme.palette.text.secondary }}
                          axisLine={{ stroke: theme.palette.divider }}
                        />
                        <YAxis 
                          tick={{ fill: theme.palette.text.secondary }}
                          axisLine={{ stroke: theme.palette.divider }}
                          unit=" tCO₂e"
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                          }}
                        />
                        <Bar 
                          dataKey="배출량" 
                          fill={theme.palette.primary.main} 
                          radius={[4, 4, 0, 0]} 
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
                    배출원 비율
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={emissionBySourceData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {emissionBySourceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
                    주요 인사이트
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            교통 배출량 감소
                          </Typography>
                          <Typography variant="body2">
                            교통 관련 탄소 배출량이 전월 대비 12% 감소했습니다. 대중교통 이용 증가가 주요 원인으로 분석됩니다.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            전기 사용량 최적화
                          </Typography>
                          <Typography variant="body2">
                            에너지 효율 개선으로 전기 사용량이 감소했으나, 난방 사용량은 5% 증가했습니다.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            목표 달성 예상
                          </Typography>
                          <Typography variant="body2">
                            현재 추세가 유지된다면, 연간 탄소 배출량 감축 목표를 달성할 확률이 85%로 예상됩니다.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* 상세 데이터 탭 */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ mb: 3, px: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  활동별 상세 배출량
                </Typography>
                <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper', borderRadius: 0 }}>
                  <Table sx={{ minWidth: 650 }} aria-label="emission data table">
                    <TableHead>
                      <TableRow>
                        <TableCell>날짜</TableCell>
                        <TableCell>활동 유형</TableCell>
                        <TableCell>수량</TableCell>
                        <TableCell align="right">배출량 (kg CO₂e)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailedEmissionData.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            {row.date}
                          </TableCell>
                          <TableCell>{row.activity}</TableCell>
                          <TableCell>{row.quantity}</TableCell>
                          <TableCell align="right">{row.emission.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              <Box sx={{ mt: 4, px: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  활동 유형별 요약
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          교통
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                          23.75 kg
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          총 125 km
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          전기
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                          15.87 kg
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          총 34.5 kWh
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          난방
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                          13.32 kg
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          총 6.0 m³
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          식품
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                          7.31 kg
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          총 4.3 kg
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
            
            {/* 추세 분석 탭 */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ px: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      월별 배출량 추세
                    </Typography>
                    <Box sx={{ height: 450 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            { month: '1월', 배출량: 58.2 },
                            { month: '2월', 배출량: 55.7 },
                            { month: '3월', 배출량: 53.5 },
                            { month: '4월', 배출량: 51.2 },
                            { month: '5월', 배출량: 49.8 },
                          ]}
                          margin={{ top: 25, right: 10, left: 15, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                          <XAxis 
                            dataKey="month" 
                            tick={{ fill: theme.palette.text.secondary }}
                            axisLine={{ stroke: theme.palette.divider }}
                          />
                          <YAxis 
                            tick={{ fill: theme.palette.text.secondary }}
                            axisLine={{ stroke: theme.palette.divider }}
                            unit=" tCO₂e"
                          />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`
                            }}
                          />
                          <Bar 
                            dataKey="배출량" 
                            fill={theme.palette.primary.main} 
                            radius={[4, 4, 0, 0]} 
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
                      추세 분석 결과
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                              감소 추세
                            </Typography>
                            <Typography variant="body2">
                              월별 탄소 배출량이 지속적으로 감소하는 추세를 보이고 있으며, 월 평균 3.2%의 감소율을 기록하고 있습니다.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                              계절적 요인
                            </Typography>
                            <Typography variant="body2">
                              난방 사용량은 계절적 요인에 따라 변동이 있으나, 전체적인 감소 추세는 유지되고 있습니다.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                              연간 예상
                            </Typography>
                            <Typography variant="body2">
                              현재 추세가 유지된다면, 연말까지 총 배출량은 약 580 tCO₂e로 예상되며, 이는 작년 대비 15% 감소한 수치입니다.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportPage;
